import React, { useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import {
  caretDownSx,
  FONT_DISPLAY,
  hairline,
  hairlineTop,
} from "@/src/components/common/broadsheet/tokens";
import DrawerMenuProps from "@/src/_types/drawer-menu-props";
import DrawerLink from "@/src/components/common/_partials/header/drawer-link/drawer-link";
import {
  drawerChildTextSx,
  drawerItemTextSx,
} from "@/src/components/common/_partials/header/drawer-link/drawer-item-styles";
import ThemeToggle from "@/src/components/main-site/_partials/header/theme-toggle/theme-toggle";

const DrawerMenu = ({
  open,
  toggleDrawer,
  menuItems,
  mainJournal,
  mainSite,
  supportUrl,
  signInUrl,
}: DrawerMenuProps) => {
  const [collapseOpen, setCollapseOpen] = useState<boolean[]>([]);

  const handleToggle = (index: number) => {
    const newCollapseOpen = [...collapseOpen];
    newCollapseOpen[index] = !newCollapseOpen[index];
    setCollapseOpen(newCollapseOpen);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => toggleDrawer(false)}
      slotProps={{
        paper: {
          sx: { backgroundColor: "custom.newsprint", backgroundImage: "none" },
        },
      }}
    >
      <Box
        sx={{ width: 250 }}
        role="presentation"
        onKeyDown={() => toggleDrawer(false)}
      >
        <List
          subheader={
            <ListSubheader
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: 0.5,
                paddingY: 0.75,
                backgroundColor: "custom.newsprint",
                ...hairline,
              }}
            >
              <Typography
                component={"h1"}
                sx={{
                  color: "custom.ink",
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: "1.125rem",
                  lineHeight: 1.2,
                }}
              >
                {mainJournal.title}
              </Typography>
            </ListSubheader>
          }
        >
          {menuItems?.map((item, index) => (
            <Box key={index}>
              {!item.children ? (
                <DrawerLink
                  color="var(--bf-ink)"
                  link={item.link || "#"}
                  key={index}
                >
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary={item.label} sx={drawerItemTextSx} />
                  </ListItem>
                </DrawerLink>
              ) : (
                <>
                  <ListItem
                    onClick={() => handleToggle(index)}
                    sx={{ py: 0.5, color: "custom.ink", cursor: "pointer" }}
                  >
                    <ListItemText primary={item.label} sx={drawerItemTextSx} />
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={caretDownSx(collapseOpen[index])}
                    />
                  </ListItem>
                  <Collapse in={collapseOpen[index]} sx={{ ml: 2 }}>
                    <List component="div" disablePadding>
                      {item.children.map((child, childIndex) => (
                        <DrawerLink
                          color="var(--bf-ink-muted)"
                          link={child.link || "#"}
                          key={childIndex}
                        >
                          <ListItem sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={child.label}
                              sx={drawerChildTextSx}
                            />
                          </ListItem>
                        </DrawerLink>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}
            </Box>
          ))}
          <DrawerLink color="var(--bf-ink)" link={supportUrl}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText primary={"Support"} sx={drawerItemTextSx} />
            </ListItem>
          </DrawerLink>
          {!mainSite && (
            <DrawerLink color="var(--bf-ink)" link={signInUrl}>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary={"Sign In"} sx={drawerItemTextSx} />
              </ListItem>
            </DrawerLink>
          )}
          <ListItem sx={{ py: 0.75, mt: 0.5, ...hairlineTop }}>
            <ThemeToggle />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default DrawerMenu;
